import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./LoginPage.module.css";

export function ForgotPasswordPage() {
  const { t, tOr } = useLocale();
  const forgotPassword = useAuth((s) => s.forgotPassword);

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [errCode, setErrCode] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrCode(null);
    try {
      await forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) setErrCode(err.code);
      else setErrCode("unknown");
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <main className={styles.shell}>
        <div className={styles.card}>
          <h1 className={styles.title}>{t("forgot.success_title")}</h1>
          <p className={styles.subtitle}>{t("forgot.success_body")}</p>
          <Link to="/login" className={styles.submit}>
            {t("forgot.success_back_to_login")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.shell}>
      <form className={styles.card} onSubmit={onSubmit}>
        <h1 className={styles.title}>{t("forgot.title")}</h1>
        <p className={styles.subtitle}>{t("forgot.subtitle")}</p>
        <label className={styles.label}>
          <span>{t("forgot.email")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        {errCode && (
          <p className={styles.error}>
            {tOr(`forgot.error.${errCode}`, "forgot.error.unknown")}
          </p>
        )}
        <button type="submit" className={styles.submit} disabled={busy}>
          {busy ? t("common.loading") : t("forgot.submit")}
        </button>
        <div className={styles.links}>
          <Link to="/login">{t("forgot.link.back_to_login")}</Link>
        </div>
      </form>
    </main>
  );
}
